import React, { useState } from "react";
import { useParsed, useGo } from "@refinedev/core";
import { CustomNavWrapper } from "@/src/components/custom";
import { CustomNavLink } from "@/src/components/custom";
import { useSidebar } from "@/src/contexts/sidebar-context";
import styles from "@scss/custom-components/nav.module.scss";

export type NavItem = {
  key: string;
  label: string;
  route?: string;
  children?: NavItem[];
};

type SideNavProps = {
  navItems: NavItem[];
};

export const CustomSiderNavGroup: React.FC<SideNavProps> = ({ navItems }) => {
  const { pathname } = useParsed();
  const go = useGo();
  const { setIsMobileMenuOpen } = useSidebar();
  const [openParent, setOpenParent] = useState<string | null>(null);

  // Helper: is this route active?
  const isRouteActive = (route?: string): boolean => {
    return Boolean(route && pathname === route);
  };

  // Helper: is this parent route active (including subroutes)?
  const isParentRouteActive = (route?: string): boolean => {
    if (!route || !pathname) return false;
    // Exact match
    if (pathname === route) return true;
    // Check if current path starts with the route (for subroutes)
    // Ensure we're matching full path segments, not partial matches
    return pathname.startsWith(route + "/");
  };

  // Helper: is any child active?
  const isChildActive = (children?: NavItem[]) =>
    children?.some((child) => isRouteActive(child.route)) ?? false;

  // Helper: is this parent open?
  const isParentOpen = (item: NavItem) =>
    openParent === item.key ||
    isParentRouteActive(item.route) ||
    isChildActive(item.children);

  // Handler for parent click
  const handleParentClick = (item: NavItem) => {
    if (item.children) {
      setOpenParent((prev) => (prev === item.key ? null : item.key));
    }
    if (item.route && !isRouteActive(item.route)) {
      go({ to: item.route, type: "replace" });
      // Close mobile menu when navigating
      setIsMobileMenuOpen(false);
    }
  };

  // Handler for child click
  const handleChildClick = (child: NavItem) => {
    if (child.route && !isRouteActive(child.route)) {
      go({ to: child.route, type: "replace" });
      // Close mobile menu when navigating
      setIsMobileMenuOpen(false);
    }
  };

  const renderNavItem = (item: NavItem) => {
    const parentActive =
      isParentRouteActive(item.route) || isChildActive(item.children);

    return (
      <CustomNavLink
        key={item.key}
        label={item.label}
        active={parentActive}
        opened={isParentOpen(item)}
        onClick={() => handleParentClick(item)}
        rightSection={
          item.children ? (
            <></>
          ) : undefined
        }
      >
        {item.children && isParentOpen(item) && (
          <div style={{ paddingBottom: 10 }}>
            {item.children.map((child) => (
              <CustomNavLink
                key={child.key}
                variant="nested"
                label={child.label}
                active={isRouteActive(child.route)}
                onClick={() => handleChildClick(child)}
              />
            ))}
          </div>
        )}
      </CustomNavLink>
    );
  };

  return (
    <CustomNavWrapper mb="md">
      {navItems.map((parent) => renderNavItem(parent))}
    </CustomNavWrapper>
  );
};
